// Decide se uma atividade deve ser apresentada ao aluno como trilha lúdica.
//
// QUEM DECIDE É O BACKEND, pelo campo `isTrailView`.
//
// O NOME IMPORTA, e é a razão de este arquivo ler `isTrailView` e não `is_trail`.
// A trilha lúdica NÃO é um domínio novo: é uma atividade comum, criada pelo fluxo
// "Minhas trilhas" do professor (`/activity/create?trail=true`, que fixa
// Matemática, sequencial e privada), apenas APRESENTADA ao aluno como trilha.
// `isTrailView` é o campo que diz exatamente isso — "esta atividade vem em
// visualização de trilha".
//
// Já `isTrail` é outro conceito no AVA: a **trilha de aprendizagem**, uma
// sequência de atividades num grafo de nós (`activity_trails`,
// `activity_trails_activities`, `activity_trails_edges`, telas em
// `pages/Activity/Trail/`). Nada disto tem a ver com aquilo — ver o aviso sobre
// os dois "Trail" no CLAUDE.md do workspace.
//
// A pegadinha: hoje os dois nomes carregam O MESMO DADO, porque o backend expõe
// `activity_activities.is_trail as isTrailView` — a coluna herdou o nome do
// conceito errado e o alias foi acrescentado depois para dizer o que ela
// significa de verdade. Este módulo lê só o alias. Ler `is_trail` funcionaria
// hoje, e foi o que ele fez até 31/07/2026, mas é depender do nome do conceito
// errado por coincidência de valor: no dia em que `is_trail` passar a significar
// trilha-de-atividades, a trilha lúdica vazaria para elas sem ninguém notar.
//
// Risco assumido: o camelCase do alias só sobrevive no Postgres porque o grammar
// do Laravel envolve o `as` em aspas duplas. Se um refactor as perder, o
// identificador é rebaixado para `istrailview` e o campo some. A falha é SEGURA —
// a trilha não aparece, o aluno cai na lista clássica e faz a atividade
// normalmente. Conferido no payload de homologação em 31/07/2026.
//
// Até 31/07/2026 este arquivo tinha duas allowlists de ids de homologação, que
// sustentaram o desenvolvimento enquanto o campo não existia. Foram removidas
// quando ele entrou no payload real.
const CAMPO_TRILHA = "isTrailView";

// A coluna é nullable de propósito: toda atividade anterior à migration — e toda
// atividade comum criada depois — chega com `null`, e cai na lista clássica.

// O valor pode chegar como booleano JSON (`true`), como tinyint do MySQL (`1`)
// ou como string, dependendo de haver ou não cast no model do Laravel. Tratar
// só `=== true` daria falso-negativo silencioso — a trilha simplesmente não
// apareceria e ninguém saberia por quê.
const VERDADEIROS = [true, 1, "1", "true"];

// Hosts em que o override de QA abaixo é honrado. Espelha os nomes que
// `config/api.js` usa para escolher o backend, mas NÃO importa de lá: o módulo
// é autocontido e nenhum import sai dele (ver ../README.md).
//
// Produção é o PADRÃO, não a exceção: um host desconhecido, ou a ausência de
// `window`, contam como produção. Errar para o lado de "é produção" fecha o
// override; errar para o outro lado o deixaria aberto para o aluno.
const HOSTS_DESENVOLVIMENTO = [
  "localhost",
  "127.0.0.1",
  "homologa",
  "alfa",
  "beta"
];

function ehProducao() {
  if (typeof window === "undefined" || !window.location) return true;
  const host = String(window.location.hostname || "").toLowerCase();
  if (!host) return true;
  return !HOSTS_DESENVOLVIMENTO.some(nome => host.includes(nome));
}

function lerParametro(location, nome) {
  const busca = location && location.search;
  if (!busca) return null;
  try {
    return new URLSearchParams(busca).get(nome);
  } catch (erro) {
    return null;
  }
}

/**
 * @param {object} activity atividade como vem de singleViewByExecution
 * @param {object} location objeto location do react-router
 * @param {object} [opcoes] `producao` existe para o teste não depender do
 *   hostname do jsdom; em runtime ninguém passa este argumento.
 * @returns {boolean}
 */
export function ehVisualizacaoTrilha(
  activity,
  location,
  { producao = ehProducao() } = {}
) {
  if (!activity) return false;

  // Override de QA/medição, e só fora de produção. Tem a palavra final ali de
  // propósito: as ferramentas de `ferramentas-diagnostico/` precisam abrir a
  // trilha numa atividade qualquer, e o cenário de não-regressão precisa do
  // inverso — ver a LISTA numa atividade que é trilha de verdade. Sem os dois
  // lados não há como comparar as duas visualizações do mesmo conteúdo.
  //
  // Em produção este bloco não existe: `is_trail` é a única coisa que liga a
  // trilha, e nenhuma query string muda a apresentação para o aluno.
  if (!producao) {
    const view = lerParametro(location, "view");
    if (view) return view === "trilha";
  }

  return VERDADEIROS.includes(activity[CAMPO_TRILHA]);
}

/**
 * A região pedida na URL (`?regiao=norte`), e só fora de produção.
 *
 * Irmão de `?view=trilha`, com a mesma guarda e pelo mesmo motivo. Quem escolhe a
 * região de verdade é o professor, na criação da trilha, e o backend a devolve em
 * `trailRegion` — mas as ferramentas de `ferramentas-diagnostico/` precisam medir
 * uma região ESPECÍFICA (os nós sobre a estrada dela, a arte sem deformação, a
 * cerimônia com as cartas dela) sem depender de existir, em homologação, uma trilha
 * criada naquela região. O `?view=trilha` já resolvia "abra como trilha numa
 * atividade qualquer"; sem este, a região daquela atividade qualquer é sempre a
 * padrão, e medir o Norte era impossível.
 *
 * Devolve o id CRU, sem validar: quem resolve é `getRegiao`, que já cai na padrão
 * para id desconhecido. Validar aqui duplicaria o catálogo dentro de `ava/`.
 *
 * Em produção devolve `null` sempre — nenhuma query string muda o que o aluno vê.
 *
 * @param {object} location objeto location do react-router
 * @param {object} [opcoes] `producao` existe para o teste não depender do hostname
 * @returns {string|null}
 */
export function regiaoForcada(location, { producao = ehProducao() } = {}) {
  if (producao) return null;
  return lerParametro(location, "regiao") || null;
}

/**
 * A chance de carta rara pedida na URL (`?raras=todas` ou `?raras=nenhuma`), e só
 * fora de produção. `null` quando ninguém pediu — e aí vale a decisão de produto.
 *
 * Terceiro irmão de `?view=trilha` e `?regiao=`, com a mesma guarda, e o que ele
 * resolve é um problema NOVO: o sorteio das raras é derivado de (aluno, atividade),
 * então uma trilha dá sempre o mesmo resultado. Quem precisa ver a CENA do arco (a
 * fase rara sem carta) numa atividade que sorteou a carta não tem como — nem
 * limpando o `localStorage`, porque não é de lá que o resultado vem.
 *
 * Sem isto, conferir os dois caminhos no AVA dependeria de achar, em homologação,
 * uma atividade cuja semente caísse do lado certo. As duas cenas da cerimônia são
 * justamente o que precisa ser visto na tela, e não em teste.
 *
 * Devolve CHANCE e não o mapa: `sortearRaras` já aceita 0 e 1, então os dois
 * extremos entram pelo caminho normal do sorteio em vez de por um desvio que
 * inventa o mapa à mão. Um caminho só é um caminho só.
 *
 * @param {object} location objeto location do react-router
 * @param {object} [opcoes] `producao` existe para o teste não depender do hostname
 * @returns {number|null} 1 (todas), 0 (nenhuma) ou null
 */
export function chanceDeRaraForcada(
  location,
  { producao = ehProducao() } = {}
) {
  if (producao) return null;

  const valor = lerParametro(location, "raras");
  if (valor === "todas") return 1;
  if (valor === "nenhuma") return 0;
  return null;
}

/**
 * Quantas cartas comuns forçar na trilha (`?cartas=<n>`), e só fora de produção.
 * `null` quando ninguém pediu — e aí vale `QUANTAS_COMUNS`.
 *
 * Quarto irmão de `?view=trilha`, `?regiao=` e `?raras=`, com a mesma guarda. O que
 * ele resolve é o mesmo problema do `?raras=`, agravado: desde 24/08/2026 a POSIÇÃO
 * da carta comum é sorteada, e o sorteio é derivado de (aluno, atividade). Uma
 * trilha dá sempre o mesmo layout, então ver "cartas espalhadas numa trilha longa" —
 * que é a única coisa que decide se o vão seco de 8 nós é aceitável (decisão 8 do
 * plano) — dependeria de achar em homologação uma atividade com o tamanho certo.
 *
 * Devolve QUANTAS e não o mapa: `sortearComuns` já aceita o `k` por parâmetro, então
 * o forçado entra pelo caminho normal do sorteio, com o mesmo estratificado e o
 * mesmo jitter. Um caminho só é um caminho só.
 *
 * O CUIDADO QUE ESTE OVERRIDE TEM E OS OUTROS NÃO: o sorteio dos comuns é GUARDADO
 * (no `localStorage` e na coluna `drawn_cards`). Se o valor forçado entrasse pelo
 * caminho normal, a primeira carga o gravaria e a segunda o leria do disco — e o
 * parâmetro pareceria não funcionar, além de deixar estado de QA no dado do aluno.
 * Por isso `TrilhaDeAtividade` usa `derivarSorteioComuns` (que não grava) e suspende
 * a sincronia enquanto ele estiver ligado.
 *
 * Só inteiro não-negativo: `?cartas=0` é um pedido válido (a trilha sem comum
 * nenhuma, que é o que 16% delas são), e por isso a validação não pode ser `||`.
 *
 * @param {object} location objeto location do react-router
 * @param {object} [opcoes] `producao` existe para o teste não depender do hostname
 * @returns {number|null}
 */
export function quantasComunsForcadas(
  location,
  { producao = ehProducao() } = {}
) {
  if (producao) return null;

  const valor = lerParametro(location, "cartas");
  if (valor === null || valor.trim() === "") return null;

  const numero = Number(valor);
  if (!Number.isInteger(numero) || numero < 0) return null;
  return numero;
}
